// Zeitpunkte aus der Antwort des Servers: Verträge beschreiben sie als `z.date()`, über JSON kommen sie als
// ISO-Text an. Der Client stellt sie anhand des AUSGABESCHEMAS des Vertrags wieder her – genau dort, wo das Schema
// einen Zeitpunkt nennt, und nirgends sonst (ein Text, der zufällig wie ein Datum aussieht, bleibt Text; ein
// Geschäftstag 'YYYY-MM-DD' ist im Vertrag ein Text und bleibt es).
//
//   const output = reviveDates(contract.output, envelope.result)
//
// Je Schema entsteht einmal ein kleiner „Wiederhersteller“ (oder null, wenn es keinen Zeitpunkt enthält – der
// häufige Fall, er kostet dann nichts). Die Attrappe für Tests (createMockClient) nutzt dieselbe Funktion: Was eine
// Seite im Test bekommt, hat dieselbe Form wie im Browser.
import type { z } from 'zod'

type Reviver = (value: unknown) => unknown

/** ISO 8601 mit Zeit und Zone – die Form von Date.prototype.toJSON() und von Postgres-Zeitpunkten in JSON. */
const ISO_DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,9})?)?(?:Z|[+-]\d{2}:?\d{2})$/

const toDate: Reviver = (value) => {
  if (typeof value !== 'string' || !ISO_DATE_TIME.test(value)) return value
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : date
}

interface Def {
  type: string
  shape?: Record<string, z.ZodType>
  catchall?: z.ZodType
  element?: z.ZodType
  innerType?: z.ZodType
  options?: readonly z.ZodType[]
  valueType?: z.ZodType
  items?: readonly z.ZodType[]
  rest?: z.ZodType | null
  left?: z.ZodType
  right?: z.ZodType
  getter?: () => z.ZodType
  in?: z.ZodType
  out?: z.ZodType
}

const cache = new WeakMap<object, Reviver | null>()
/** Schemata in Arbeit (rekursive Schemata über z.lazy): Der Verweis wird erst beim Aufruf aufgelöst. */
const pending = new WeakSet<object>()

const defOf = (schema: z.ZodType): Def | null => (schema as unknown as { _zod?: { def?: Def } })?._zod?.def ?? null

function compile(schema: z.ZodType): Reviver | null {
  if (cache.has(schema)) return cache.get(schema) ?? null
  // Rekursion: später über den Zwischenspeicher nachschlagen
  if (pending.has(schema)) return (value) => (cache.get(schema) ?? ((same: unknown) => same))(value)
  pending.add(schema)
  const reviver = build(schema)
  pending.delete(schema)
  cache.set(schema, reviver)
  return reviver
}

function build(schema: z.ZodType): Reviver | null {
  const def = defOf(schema)
  if (!def) return null
  switch (def.type) {
    case 'date':
      return toDate
    case 'optional':
    case 'nullable':
    case 'default':
    case 'prefault':
    case 'nonoptional':
    case 'readonly':
    case 'catch':
      return def.innerType ? compile(def.innerType) : null
    case 'lazy':
      return def.getter ? compile(def.getter()) : null
    case 'pipe':
      // die Ausgabe des Servers hat die Form NACH der Umwandlung
      return def.out ? compile(def.out) : null
    case 'array': {
      const element = def.element ? compile(def.element) : null
      if (!element) return null
      return (value) => (Array.isArray(value) ? value.map(element) : value)
    }
    case 'tuple': {
      const items = (def.items ?? []).map((item) => compile(item))
      const rest = def.rest ? compile(def.rest) : null
      if (!rest && items.every((item) => item === null)) return null
      return (value) => (Array.isArray(value) ? value.map((entry: unknown, index) => (items[index] ?? (index >= items.length ? rest : null) ?? ((same: unknown) => same))(entry)) : value)
    }
    case 'object': {
      const fields = Object.entries(def.shape ?? {}).flatMap(([key, field]) => {
        const reviver = compile(field)
        return reviver ? [[key, reviver] as const] : []
      })
      const catchall = def.catchall ? compile(def.catchall) : null
      if (fields.length === 0 && !catchall) return null
      const known = new Set(Object.keys(def.shape ?? {}))
      return (value) => {
        if (value === null || typeof value !== 'object' || Array.isArray(value)) return value
        const record = value as Record<string, unknown>
        let out: Record<string, unknown> | null = null
        const set = (key: string, next: unknown): void => {
          if (next === record[key]) return
          out ??= { ...record }
          out[key] = next
        }
        for (const [key, reviver] of fields) if (key in record) set(key, reviver(record[key]))
        if (catchall) for (const key of Object.keys(record)) if (!known.has(key)) set(key, catchall(record[key]))
        return out ?? value
      }
    }
    case 'record': {
      const entry = def.valueType ? compile(def.valueType) : null
      if (!entry) return null
      return (value) => {
        if (value === null || typeof value !== 'object' || Array.isArray(value)) return value
        return Object.fromEntries(Object.entries(value as Record<string, unknown>).map(([key, item]) => [key, entry(item)]))
      }
    }
    case 'union': {
      // jede Möglichkeit darf ihre Zeitpunkte herstellen; umgewandelt wird nur, was wie ein Zeitpunkt aussieht
      const options = (def.options ?? []).map((option) => compile(option)).filter((option): option is Reviver => option !== null)
      if (options.length === 0) return null
      return (value) => options.reduce((current, option) => option(current), value)
    }
    case 'intersection': {
      const sides = [def.left, def.right].map((side) => (side ? compile(side) : null)).filter((side): side is Reviver => side !== null)
      if (sides.length === 0) return null
      return (value) => sides.reduce((current, side) => side(current), value)
    }
    default:
      return null
  }
}

/**
 * Stellt in `value` die Zeitpunkte her, die `schema` als `z.date()` nennt. Was schon ein Date ist, bleibt; was nicht
 * zum Schema passt, bleibt unverändert (geprüft hat der Server). Ohne Zeitpunkt im Schema kommt `value` selbst zurück.
 */
export function reviveDates<T = unknown>(schema: z.ZodType | null | undefined, value: T): T {
  if (!schema || value === null || value === undefined) return value
  const reviver = compile(schema)
  return reviver ? (reviver(value) as T) : value
}

/** true, wenn das Schema irgendwo einen Zeitpunkt nennt (für Tests und Diagnose). */
export const hasDates = (schema: z.ZodType): boolean => compile(schema) !== null
